import React from "react";
import { Play } from "@phosphor-icons/react";

const PRESETS = [
  { id: "macd_bull_cross", label: "MACD Bullish Cross" },
  { id: "rsi_oversold_bounce", label: "RSI Oversold Bounce" },
  { id: "vol_surge", label: "Volume Surge ≥1.8×" },
  { id: "top_decile", label: "Top Decile 5D Return" },
];

const INDICATORS = ["rsi", "macd_hist", "price_change_5d", "volume_surge"];
const OPS = [
  { v: "gt", label: ">" }, { v: "lt", label: "<" },
  { v: "cross_up", label: "crosses ↑" }, { v: "cross_down", label: "crosses ↓" },
];

function modeBtnClass(active) {
  return active ? "bg-white text-black" : "border-[#222] text-[#8a8a8e]";
}

function presetBtnClass(active) {
  return active ? "border-[#00e676] text-[#00e676]" : "border-[#222] text-[#8a8a8e] hover:text-white";
}

function PresetMode({ preset, onPresetChange }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {PRESETS.map((p) => (
        <button
          key={p.id}
          onClick={() => onPresetChange(p.id)}
          data-testid={`bt-preset-${p.id}`}
          className={`p-3 text-left text-xs font-mono-data border ${presetBtnClass(preset === p.id)}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function CustomMode({ rules, onSetRule, onAddRule, onDelRule }) {
  return (
    <div className="space-y-2">
      {rules.map((r) => (
        <div key={r.id} className="flex gap-2 items-center">
          <select
            value={r.indicator}
            onChange={(e) => onSetRule(r.id, "indicator", e.target.value)}
            className="px-2 py-1 bg-[#0a0a0a] border border-[#222] text-xs font-mono-data"
          >
            {INDICATORS.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select
            value={r.op}
            onChange={(e) => onSetRule(r.id, "op", e.target.value)}
            className="px-2 py-1 bg-[#0a0a0a] border border-[#222] text-xs font-mono-data"
          >
            {OPS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
          </select>
          <input
            type="number" value={r.threshold}
            onChange={(e) => onSetRule(r.id, "threshold", e.target.value)}
            className="px-2 py-1 bg-[#0a0a0a] border border-[#222] text-xs font-mono-data w-24"
          />
          <button onClick={() => onDelRule(r.id)} className="text-[#ff3b30] text-xs px-2">×</button>
        </div>
      ))}
      <button onClick={onAddRule} className="text-xs font-mono-data text-[#8a8a8e] hover:text-white">+ ADD RULE</button>
    </div>
  );
}

export default function BacktestForm({
  symbol, onSymbolChange,
  hold, onHoldChange,
  period, onPeriodChange,
  volumePref, onVolumePrefChange,
  mode, onModeChange,
  preset, onPresetChange,
  rules, onSetRule, onAddRule, onDelRule,
  loading, onRun,
}) {
  return (
    <>
      <div className="panel p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <div className="micro-label mb-1">Ticker</div>
          <input
            value={symbol}
            onChange={(e) => onSymbolChange(e.target.value.toUpperCase())}
            data-testid="bt-symbol"
            className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-[#222] outline-none font-mono-data text-sm uppercase"
          />
        </div>
        <div>
          <div className="micro-label mb-1">Hold Days</div>
          <input
            type="number" value={hold}
            onChange={(e) => onHoldChange(e.target.value)}
            data-testid="bt-hold"
            className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-[#222] outline-none font-mono-data text-sm"
          />
        </div>
        <div>
          <div className="micro-label mb-1">Period</div>
          <select
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            data-testid="bt-period"
            className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-[#222] outline-none font-mono-data text-sm"
          >
            <option value="6mo">6mo</option>
            <option value="1y">1y</option>
            <option value="2y">2y</option>
            <option value="5y">5y</option>
          </select>
        </div>
        <div>
          <div className="micro-label mb-1">Volume Filter</div>
          <select
            value={volumePref}
            onChange={(e) => onVolumePrefChange(e.target.value)}
            data-testid="bt-volume-pref"
            title="Require volume confirmation on signal day"
            className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-[#222] outline-none font-mono-data text-sm"
          >
            <option value="ignore">IGNORE</option>
            <option value="standard">STANDARD</option>
            <option value="volume_driven">DRIVEN (≥1.2×)</option>
            <option value="surge_only">SURGE ONLY (≥1.5×)</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={onRun} disabled={loading}
            data-testid="bt-run"
            className="w-full flex items-center justify-center gap-1 py-1.5 bg-[#00e676] text-black text-xs font-mono-data uppercase tracking-widest hover:bg-white disabled:opacity-50"
          >
            <Play size={12} weight="fill" />{loading ? "RUNNING…" : "RUN"}
          </button>
        </div>
      </div>

      <div className="panel p-4 mb-4">
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => onModeChange("preset")}
            data-testid="bt-mode-preset"
            className={`px-3 py-1 text-xs font-mono-data uppercase tracking-wider border ${modeBtnClass(mode === "preset")}`}
          >Preset</button>
          <button
            onClick={() => onModeChange("custom")}
            data-testid="bt-mode-custom"
            className={`px-3 py-1 text-xs font-mono-data uppercase tracking-wider border ${modeBtnClass(mode === "custom")}`}
          >Custom</button>
        </div>
        {mode === "preset" ? (
          <PresetMode preset={preset} onPresetChange={onPresetChange} />
        ) : (
          <CustomMode rules={rules} onSetRule={onSetRule} onAddRule={onAddRule} onDelRule={onDelRule} />
        )}
      </div>
    </>
  );
}
